// Audit Results Calculation Logic - Goal & Pain-based Analysis

// Industry-specific automation opportunities and benchmarks
const industryData = {
  "Agency / Consulting": {
    avgTimeSaved: "12-18 hours/week",
    avgCostSaved: "₹50,000 - ₹90,000/month",
    topOpportunities: [
      "Automated client reporting",
      "Lead follow-up sequences",
      "Project status updates",
      "Social media scheduling"
    ],
    caseStudy: "A digital agency automated their client reporting and saved 15 hours every week - that's almost 2 full work days!",
    painToSolution: {
      "scale": "Systems that handle 3x clients without 3x effort",
      "repetitive": "Auto-generated reports and status updates for clients",
      "response": "Instant auto-responses and smart routing for enquiries",
      "leads": "Automated lead nurturing sequences so no prospect falls through the cracks",
      "copy-paste": "Connected systems that share data automatically",
      "day-off": "Workflows that run without you being present"
    },
    departmentSolutions: {
      "Sales & Marketing": "Automated lead scoring, follow-up sequences, and campaign reporting",
      "Accounting & Finance": "Auto-invoicing, payment reminders, and expense tracking",
      "HR & Admin": "Employee onboarding workflows and timesheet automation",
      "Operations & Delivery": "Project status updates and client deliverable tracking",
      "Customer Support": "Ticket routing and automated response templates"
    }
  },
  "E-commerce / Retail": {
    avgTimeSaved: "15-25 hours/week",
    avgCostSaved: "₹60,000 - ₹1,20,000/month",
    topOpportunities: [
      "Order processing automation",
      "Inventory low-stock alerts",
      "Customer follow-up sequences",
      "Review request automation"
    ],
    caseStudy: "An online store automated order confirmations and shipping updates - customer complaints dropped by 60%.",
    painToSolution: {
      "scale": "Order processing that handles 10x volume automatically",
      "repetitive": "Auto-sync inventory across all platforms",
      "response": "Instant order confirmations and shipping updates",
      "leads": "Abandoned cart recovery sequences that bring customers back",
      "copy-paste": "Orders flow automatically from website to shipping",
      "day-off": "24/7 order processing without manual intervention"
    },
    departmentSolutions: {
      "Sales & Marketing": "Abandoned cart recovery, review requests, and promotional campaigns",
      "Accounting & Finance": "Auto-reconciliation, invoice generation, and payment tracking",
      "HR & Admin": "Staff scheduling and performance tracking",
      "Operations & Delivery": "Order routing, inventory alerts, and shipping automation",
      "Customer Support": "Order status updates and return processing automation"
    }
  },
  "Professional Services (CA, Legal, etc.)": {
    avgTimeSaved: "18-28 hours/week",
    avgCostSaved: "₹70,000 - ₹1,50,000/month",
    topOpportunities: [
      "Document collection automation",
      "Compliance deadline reminders",
      "Client onboarding workflows",
      "Invoice and payment follow-ups"
    ],
    caseStudy: "A CA firm automated document collection from clients - onboarding time dropped from 5 days to just 1 day.",
    painToSolution: {
      "scale": "Handle more clients with the same team through smart workflows",
      "repetitive": "Document collection workflows that chase clients for you",
      "response": "Instant acknowledgments and status updates for clients",
      "leads": "Automated follow-ups with prospects who enquired",
      "copy-paste": "Client data flows from forms to accounting software",
      "day-off": "Automated reminders keep clients on track"
    },
    departmentSolutions: {
      "Sales & Marketing": "Lead nurturing and referral program automation",
      "Accounting & Finance": "Billing automation, payment follow-ups, and expense management",
      "HR & Admin": "Staff allocation, leave management, and compliance training reminders",
      "Operations & Delivery": "Document collection, deadline tracking, and client deliverables",
      "Customer Support": "Client query routing and status update automation"
    }
  },
  "Healthcare / Clinic": {
    avgTimeSaved: "12-20 hours/week",
    avgCostSaved: "₹45,000 - ₹95,000/month",
    topOpportunities: [
      "Appointment reminders",
      "Patient follow-up automation",
      "Prescription/report delivery",
      "Review collection"
    ],
    caseStudy: "A clinic automated appointment reminders via WhatsApp - no-shows dropped by 65%, adding ₹80,000/month in recovered revenue.",
    painToSolution: {
      "scale": "See more patients without adding reception staff",
      "repetitive": "Patient reminders and follow-ups run automatically",
      "response": "Instant appointment confirmations and report delivery",
      "leads": "Automated responses to new patient enquiries",
      "copy-paste": "Patient info syncs between booking and records",
      "day-off": "Reminders go out even when you're not there"
    },
    departmentSolutions: {
      "Sales & Marketing": "Patient acquisition campaigns and referral program automation",
      "Accounting & Finance": "Billing, insurance claims, and payment follow-ups",
      "HR & Admin": "Staff scheduling, credential tracking, and compliance reminders",
      "Operations & Delivery": "Appointment scheduling, patient flow, and report delivery",
      "Customer Support": "Patient query handling and feedback collection"
    }
  },
  "Manufacturing": {
    avgTimeSaved: "15-25 hours/week",
    avgCostSaved: "₹55,000 - ₹1,20,000/month",
    topOpportunities: [
      "Inventory tracking & alerts",
      "Order processing automation",
      "Vendor communication",
      "Production scheduling"
    ],
    caseStudy: "A manufacturing business automated inventory alerts and purchase orders - stockouts reduced by 85%, saving lakhs in lost sales.",
    painToSolution: {
      "scale": "Handle 3x orders without proportional admin increase",
      "repetitive": "Purchase orders generated from low-stock alerts",
      "response": "Instant order confirmations and delivery updates to customers",
      "leads": "Quote follow-ups sent automatically",
      "copy-paste": "Orders sync between WhatsApp, spreadsheets & accounting",
      "day-off": "Automated reorder triggers keep business running"
    },
    departmentSolutions: {
      "Sales & Marketing": "Quote generation, follow-ups, and customer relationship management",
      "Accounting & Finance": "Purchase orders, invoice matching, and vendor payments",
      "HR & Admin": "Attendance tracking, shift scheduling, and compliance documentation",
      "Operations & Delivery": "Inventory alerts, production scheduling, and dispatch tracking",
      "Customer Support": "Order status updates and delivery coordination"
    }
  },
  "Education / Coaching": {
    avgTimeSaved: "10-18 hours/week",
    avgCostSaved: "₹35,000 - ₹75,000/month",
    topOpportunities: [
      "Student communication automation",
      "Assignment/class reminders",
      "Payment follow-ups",
      "Certificate generation"
    ],
    caseStudy: "A coaching institute automated class reminders and payment follow-ups - attendance improved 40% and payment delays dropped 70%.",
    painToSolution: {
      "scale": "Enrol more students without adding admin staff",
      "repetitive": "Class schedules and reminders sent automatically",
      "response": "Instant responses to student and parent queries",
      "leads": "Enquiry follow-ups that convert more students",
      "copy-paste": "Student data syncs between forms and records",
      "day-off": "Reminders and updates continue without you"
    },
    departmentSolutions: {
      "Sales & Marketing": "Enquiry follow-ups, course promotions, and referral programs",
      "Accounting & Finance": "Fee collection, payment reminders, and expense tracking",
      "HR & Admin": "Teacher scheduling, attendance, and performance reviews",
      "Operations & Delivery": "Class scheduling, assignment tracking, and certificate generation",
      "Customer Support": "Parent communication and feedback collection"
    }
  },
  "CA / Accounting Firms": {
    avgTimeSaved: "18-28 hours/week",
    avgCostSaved: "₹70,000 - ₹1,50,000/month",
    topOpportunities: [
      "GSTR reconciliation and mismatch detection",
      "Bank statement to ledger auto-matching",
      "Client document collection automation",
      "Compliance deadline tracking and reminders"
    ],
    caseStudy: "A CA firm automated GST reconciliation and client document collection - onboarding time dropped from 5 days to 1 day, and peak season overtime reduced by 60%.",
    painToSolution: {
      "scale": "Handle 3x clients during peak season with the same team",
      "repetitive": "GST reconciliation, bank matching, and data entry run automatically",
      "response": "Instant acknowledgments and ITR status updates for clients",
      "leads": "Automated follow-ups with businesses who enquired about your services",
      "copy-paste": "Client data flows from forms straight into Tally or Zoho",
      "day-off": "Compliance reminders and client follow-ups run without you"
    },
    departmentSolutions: {
      "Sales & Marketing": "Referral program automation and lead nurturing for new client acquisition",
      "Accounting & Finance": "GSTR reconciliation, TDS tracking, invoice generation, and payment follow-ups",
      "HR & Admin": "Staff allocation during peak season, leave management, and article training tracking",
      "Operations & Delivery": "Document collection from clients, deadline tracking, and bulk return processing",
      "Customer Support": "Client query routing, ITR status updates, and compliance calendar sharing"
    }
  },
  "Legal / Law Firms": {
    avgTimeSaved: "15-25 hours/week",
    avgCostSaved: "₹60,000 - ₹1,30,000/month",
    topOpportunities: [
      "Document drafting from templates",
      "Court date and filing deadline tracking",
      "Client communication and status updates",
      "Billing and payment follow-ups"
    ],
    caseStudy: "A law firm automated court deadline tracking and client document collection - missed deadlines dropped to zero and client onboarding time reduced by 70%.",
    painToSolution: {
      "scale": "Take on more cases without hiring additional associates",
      "repetitive": "Standard notices, agreements, and replies generated from templates in minutes",
      "response": "Instant case status updates and document acknowledgments for clients",
      "leads": "Automated follow-ups with prospective clients who made enquiries",
      "copy-paste": "Client data flows from intake forms to case management system",
      "day-off": "Court date reminders and filing deadlines tracked automatically"
    },
    departmentSolutions: {
      "Sales & Marketing": "Client referral programs and follow-up sequences for new consultations",
      "Accounting & Finance": "Time-based billing automation, invoice generation, and payment reminders",
      "HR & Admin": "Associate task allocation, leave management, and bar compliance tracking",
      "Operations & Delivery": "Court date tracking, document drafting workflows, and case file management",
      "Customer Support": "Client portal for case status, document sharing, and appointment scheduling"
    }
  },
  "Real Estate": {
    avgTimeSaved: "12-22 hours/week",
    avgCostSaved: "₹50,000 - ₹1,10,000/month",
    topOpportunities: [
      "Instant lead capture from portals and social media",
      "Automated lead nurturing and property matching",
      "Site visit scheduling and follow-up",
      "Document collection and RERA compliance tracking"
    ],
    caseStudy: "A real estate broker automated lead follow-ups from 99acres and MagicBricks - response time dropped from 6 hours to 2 minutes, and conversions increased by 40%.",
    painToSolution: {
      "scale": "Manage 3x property listings and leads without adding agents",
      "repetitive": "Property updates, price alerts, and new listing notifications sent automatically",
      "response": "Instant responses to portal enquiries before competitors reach the lead",
      "leads": "Automated nurturing sequences so no prospect goes cold",
      "copy-paste": "Leads from 99acres, MagicBricks, and social media flow into one system",
      "day-off": "Site visit reminders and lead follow-ups continue without you"
    },
    departmentSolutions: {
      "Sales & Marketing": "Lead capture from all portals, automated property matching, and nurturing sequences",
      "Accounting & Finance": "Brokerage tracking, commission calculations, and payment follow-ups",
      "HR & Admin": "Agent performance tracking, site visit allocation, and attendance management",
      "Operations & Delivery": "Site visit scheduling, document collection, and RERA compliance workflows",
      "Customer Support": "Buyer query handling, property shortlisting updates, and feedback collection"
    }
  },
  "Other": {
    avgTimeSaved: "10-18 hours/week",
    avgCostSaved: "₹40,000 - ₹80,000/month",
    topOpportunities: [
      "Email and communication automation",
      "Task reminders and follow-ups",
      "Data entry elimination",
      "Report generation"
    ],
    caseStudy: "Businesses across industries typically save 10-20 hours per week by automating their most repetitive tasks.",
    painToSolution: {
      "scale": "Build systems that grow with your business",
      "repetitive": "Reports and updates generated automatically",
      "response": "Quick auto-responses while you focus on important work",
      "leads": "Never miss a follow-up with automated sequences",
      "copy-paste": "Systems talk to each other so you don't have to",
      "day-off": "Workflows run on autopilot"
    },
    departmentSolutions: {
      "Sales & Marketing": "Lead management and marketing campaign automation",
      "Accounting & Finance": "Invoice generation, payment tracking, and financial reporting",
      "HR & Admin": "Employee onboarding, leave management, and policy reminders",
      "Operations & Delivery": "Task management, workflow automation, and status tracking",
      "Customer Support": "Query routing, response templates, and feedback collection"
    }
  }
};

// Updated pain point and goal analysis for Q3
const painPointAnalysis = {
  // Goals (positive/aspirational)
  "I want to scale operations without increasing headcount": {
    key: "scale",
    severity: 3,
    isGoal: true,
    growth_focused: true,
    solution: "Scalable Workflow Automation",
    hours_saved: 10,
    insight: "You're thinking about growth - automation is perfect for scaling without proportional cost increases"
  },
  "I want to free my team from repetitive work": {
    key: "repetitive",
    severity: 3,
    isGoal: true,
    efficiency_focused: true,
    solution: "Task Automation & Process Streamlining",
    hours_saved: 12,
    insight: "Your team's time is valuable - let's redirect it from repetitive tasks to high-value work"
  },
  "I want faster response times for customers": {
    key: "response",
    severity: 2,
    isGoal: true,
    customer_focused: true,
    solution: "Customer Communication Automation",
    hours_saved: 6,
    insight: "Speed matters in business - automated responses can cut your response time to seconds"
  },
  // Problems (pain points)
  "I forget to follow up with leads and lose business": {
    key: "leads",
    severity: 3,
    isProblem: true,
    revenue_impact: true,
    solution: "Lead Follow-up Automation",
    hours_saved: 5,
    insight: "You're likely losing 20-30% of potential revenue from missed follow-ups"
  },
  "I copy-paste the same information into multiple places": {
    key: "copy-paste",
    severity: 2,
    isProblem: true,
    efficiency_issue: true,
    solution: "System Integration & Data Sync",
    hours_saved: 6,
    insight: "Manual data entry is error-prone and time-consuming - automation eliminates both"
  },
  "I can't take a day off without things falling apart": {
    key: "day-off",
    severity: 3,
    isProblem: true,
    quality_of_life: true,
    solution: "Autonomous Workflow Setup",
    hours_saved: 10,
    insight: "Your business is too dependent on you - automation helps build systems that run without you"
  }
};

// Department mapping for Q4 - tailored recommendations
const departmentMapping = {
  "Sales & Marketing": {
    focus: "revenue",
    icon: "TrendingUp",
    primarySolutions: [
      "Lead scoring and automated follow-ups",
      "Marketing campaign automation",
      "CRM data management and reporting"
    ],
    quickWin: "Automated lead follow-up sequences",
    impact: "Typically increases conversion rates by 20-35%"
  },
  "Accounting & Finance": {
    focus: "efficiency",
    icon: "Calculator",
    primarySolutions: [
      "Invoice generation and payment reminders",
      "Expense tracking and approval workflows",
      "Financial reporting automation"
    ],
    quickWin: "Automated invoice and payment reminder system",
    impact: "Reduces payment delays by 40-60%"
  },
  "HR & Admin": {
    focus: "operations",
    icon: "Users",
    primarySolutions: [
      "Employee onboarding automation",
      "Leave management and approvals",
      "Document collection and compliance tracking"
    ],
    quickWin: "Automated employee onboarding workflow",
    impact: "Cuts onboarding time by 50-70%"
  },
  "Operations & Delivery": {
    focus: "efficiency",
    icon: "Settings",
    primarySolutions: [
      "Task assignment and tracking",
      "Status updates and notifications",
      "Inventory and resource management"
    ],
    quickWin: "Automated status updates and task reminders",
    impact: "Improves on-time delivery by 30-50%"
  },
  "Customer Support": {
    focus: "customer",
    icon: "MessageCircle",
    primarySolutions: [
      "Ticket routing and prioritisation",
      "Automated response templates",
      "Feedback collection and analysis"
    ],
    quickWin: "Smart ticket routing with auto-responses",
    impact: "Reduces response time by 60-80%"
  }
};

// Team size multipliers
const teamMultipliers = {
  "Just me": { multiplier: 1, hourlyValue: 500, context: "solopreneur" },
  "2-5": { multiplier: 1.5, hourlyValue: 400, context: "small team" },
  "6-20": { multiplier: 2.5, hourlyValue: 350, context: "growing team" },
  "21-50": { multiplier: 4, hourlyValue: 300, context: "medium business" },
  "50+": { multiplier: 6, hourlyValue: 250, context: "larger organisation" }
};

// ==============================================
// VERTICAL-SPECIFIC DATA: CA / Accounting Firms
// ==============================================

const caFirmsPainPoints = {
  "We miss compliance deadlines and have to pay penalties": {
    key: "compliance-deadlines",
    severity: 3,
    solution: "AI Deadline Tracking & Compliance Automation",
    hours_saved: 8,
    insight: "73% of CA firms miss 2-3 deadlines annually, costing Rs 85,000+ in penalties — automation ensures nothing slips through"
  },
  "GST reconciliation and TDS matching errors eat up days every month": {
    key: "gst-reconciliation",
    severity: 3,
    solution: "Automated GST/TDS Reconciliation",
    hours_saved: 12,
    insight: "Manual reconciliation has a 45% error rate — automation cuts reconciliation time by 70-80% with near-zero errors"
  },
  "Countless hours go into manual data entry and document chasing": {
    key: "data-entry",
    severity: 3,
    solution: "Automated Document Collection & Data Entry",
    hours_saved: 10,
    insight: "25% of your team's week goes into manual work that AI can handle — reclaim those hours for advisory work"
  },
  "Can't take on new clients without hiring — talent is impossible to find": {
    key: "talent-scaling",
    severity: 3,
    solution: "Scalable Workflow Automation",
    hours_saved: 10,
    insight: "With 300K professionals leaving accounting and 72% being solo practitioners, automation lets you scale without hiring"
  },
  "Peak season burnout — 16-hour days, errors cascade, staff quit": {
    key: "peak-burnout",
    severity: 3,
    solution: "Bulk Processing & Error Detection",
    hours_saved: 15,
    insight: "Peak season overtime drives attrition — automation handles volume while maintaining accuracy under pressure"
  },
  "Clients want advisory and dashboards, not just year-end compliance": {
    key: "advisory-gap",
    severity: 2,
    solution: "AI-Powered Advisory Insights",
    hours_saved: 6,
    insight: "62% of clients want more than compliance — AI spots advisory opportunities from transaction patterns, increasing revenue per client by 20-30%"
  }
};

const caFirmsWorkStreams = {
  "GST & Indirect Tax": {
    focus: "compliance",
    icon: "FileText",
    primarySolutions: [
      "GSTR-2A/2B reconciliation with auto mismatch detection",
      "GST return preparation and filing automation",
      "E-way bill generation and tracking"
    ],
    quickWin: "Automated GSTR reconciliation that flags mismatches before filing",
    impact: "Cuts reconciliation time by 70-80% with near-zero errors"
  },
  "Income Tax & TDS": {
    focus: "compliance",
    icon: "Calculator",
    primarySolutions: [
      "Bulk ITR data collection and validation",
      "TDS computation, payment tracking, and return filing",
      "Advance tax calculations and reminders"
    ],
    quickWin: "Automated TDS tracking with challan payment reminders",
    impact: "Zero missed TDS deadlines and penalty-free compliance"
  },
  "Audit & Assurance": {
    focus: "risk",
    icon: "Shield",
    primarySolutions: [
      "Audit checklist automation with progress tracking",
      "Bank reconciliation and ledger matching",
      "Compliance deadline calendar across all clients"
    ],
    quickWin: "Automated compliance calendar with client-wise deadline alerts",
    impact: "Zero missed deadlines and audit-ready files always"
  },
  "Client Management & Onboarding": {
    focus: "efficiency",
    icon: "UserPlus",
    primarySolutions: [
      "Digital onboarding forms with WhatsApp auto-reminders",
      "Document checklist with automated follow-ups",
      "Client communication and status update workflows"
    ],
    quickWin: "WhatsApp-based document collection with auto-reminders",
    impact: "Reduces client onboarding from 5 days to 1 day"
  },
  "Advisory & Consulting": {
    focus: "value",
    icon: "BarChart",
    primarySolutions: [
      "Automated MIS report generation from Tally/Zoho data",
      "Client-facing dashboards with real-time financial insights",
      "AI-powered advisory opportunity detection from transaction patterns"
    ],
    quickWin: "Automated monthly MIS reports sent to clients",
    impact: "Positions you as strategic advisor — increases revenue per client by 20-30%"
  }
};

// ==============================================
// VERTICAL-SPECIFIC DATA: Legal / Law Firms
// ==============================================

const legalPainPoints = {
  "Drafting (notices, agreements, replies)": {
    key: "drafting",
    severity: 3,
    solution: "Template-Based Document Generation",
    hours_saved: 10,
    insight: "You're spending billable hours on repetitive drafting instead of strategy"
  },
  "Tracking court dates and filing deadlines": {
    key: "court-dates",
    severity: 3,
    solution: "Automated Court Calendar & Deadline Alerts",
    hours_saved: 6,
    insight: "Missing a deadline can cost you the case — automation ensures zero misses"
  },
  "Chasing clients for documents and signatures": {
    key: "document-chase",
    severity: 3,
    solution: "Automated Document Collection & e-Signature Workflows",
    hours_saved: 8,
    insight: "Your associates shouldn't be doing admin — let automation chase documents"
  },
  "Billing and tracking billable hours": {
    key: "billing",
    severity: 2,
    solution: "Time Tracking & Automated Billing",
    hours_saved: 5,
    insight: "Most firms under-bill by 15-20% due to poor time tracking"
  },
  "Client onboarding and intake paperwork": {
    key: "onboarding",
    severity: 2,
    solution: "Digital Client Intake & KYC Automation",
    hours_saved: 6,
    insight: "First impressions matter — streamlined onboarding builds trust"
  },
  "Managing case files across multiple matters": {
    key: "case-management",
    severity: 2,
    solution: "Case Management & File Organization",
    hours_saved: 4,
    insight: "Time spent searching for files is time not spent on cases"
  }
};

const legalWorkStreams = {
  "Litigation & Court Work": {
    focus: "compliance",
    icon: "Scale",
    primarySolutions: [
      "Court date and hearing calendar automation",
      "Filing deadline tracking with alerts",
      "Case status updates to clients"
    ],
    quickWin: "Automated court calendar with deadline alerts",
    impact: "Zero missed deadlines and clients always informed"
  },
  "Contract Drafting & Review": {
    focus: "efficiency",
    icon: "FileText",
    primarySolutions: [
      "Template-based document generation",
      "Clause library with quick insertion",
      "Version control and change tracking"
    ],
    quickWin: "Standard agreement templates with auto-fill",
    impact: "Draft routine contracts in minutes instead of hours"
  },
  "Client Onboarding & Documentation": {
    focus: "experience",
    icon: "UserPlus",
    primarySolutions: [
      "Digital intake forms with e-signatures",
      "Automated document collection via WhatsApp",
      "KYC verification workflows"
    ],
    quickWin: "WhatsApp-based document collection with reminders",
    impact: "Onboard clients in 1 day instead of 1 week"
  },
  "Billing & Collections": {
    focus: "revenue",
    icon: "CreditCard",
    primarySolutions: [
      "Time tracking with auto-capture",
      "Automated invoice generation",
      "Payment reminder sequences"
    ],
    quickWin: "Automated billing with time-based invoice generation",
    impact: "Capture 20% more billable hours and reduce payment delays"
  },
  "Case Management & Filing": {
    focus: "organization",
    icon: "Folder",
    primarySolutions: [
      "Centralized case file management",
      "Document tagging and search",
      "Matter-wise task tracking"
    ],
    quickWin: "Organized digital case files with instant search",
    impact: "Find any document in seconds, not minutes"
  },
  "Client Communication & Updates": {
    focus: "relationship",
    icon: "MessageCircle",
    primarySolutions: [
      "Automated case status updates",
      "Scheduled check-in reminders",
      "Client portal for document sharing"
    ],
    quickWin: "Automated status updates after each hearing",
    impact: "Clients feel informed without you spending time on calls"
  }
};

// ==============================================
// VERTICAL-SPECIFIC DATA: Real Estate
// ==============================================

const realEstatePainPoints = {
  "Responding to leads from portals before competitors": {
    key: "lead-response",
    severity: 3,
    solution: "Instant Lead Capture & Auto-Response",
    hours_saved: 8,
    insight: "The first agent to respond wins 78% of the time — speed is everything"
  },
  "Following up with cold/warm leads": {
    key: "lead-followup",
    severity: 3,
    solution: "Automated Lead Nurturing Sequences",
    hours_saved: 10,
    insight: "Most deals close after 5-7 follow-ups — automation ensures you never give up"
  },
  "Scheduling and coordinating site visits": {
    key: "site-visits",
    severity: 2,
    solution: "Automated Site Visit Scheduling",
    hours_saved: 5,
    insight: "No-shows waste your time — automated reminders reduce them by 60%"
  },
  "Tracking deal progress across multiple buyers": {
    key: "deal-tracking",
    severity: 2,
    solution: "Visual Pipeline & Deal Tracking",
    hours_saved: 4,
    insight: "Knowing where each deal stands helps you focus on the ones most likely to close"
  },
  "Collecting documents for registration/RERA": {
    key: "document-collection",
    severity: 2,
    solution: "Automated Document Collection Workflows",
    hours_saved: 6,
    insight: "Chasing documents delays closings — automation keeps things moving"
  },
  "Managing broker/channel partner commissions": {
    key: "commissions",
    severity: 2,
    solution: "Commission Tracking & Payout Automation",
    hours_saved: 4,
    insight: "Accurate commission tracking keeps your partners happy and motivated"
  }
};

const realEstateWorkStreams = {
  "Lead Capture & Response": {
    focus: "speed",
    icon: "Zap",
    primarySolutions: [
      "Instant lead capture from 99acres, MagicBricks, Housing",
      "Auto-response within 2 minutes",
      "Lead routing to available agents"
    ],
    quickWin: "Instant WhatsApp response to portal enquiries",
    impact: "Respond before competitors — win more leads"
  },
  "Lead Nurturing & Follow-ups": {
    focus: "conversion",
    icon: "TrendingUp",
    primarySolutions: [
      "Automated follow-up sequences via WhatsApp/Email",
      "Property matching and alerts",
      "Re-engagement campaigns for cold leads"
    ],
    quickWin: "7-day automated nurturing sequence for new leads",
    impact: "Convert 40% more leads with consistent follow-up"
  },
  "Site Visit Scheduling": {
    focus: "efficiency",
    icon: "Calendar",
    primarySolutions: [
      "Online booking with availability sync",
      "Automated confirmation and reminders",
      "No-show follow-up sequences"
    ],
    quickWin: "Automated site visit reminders 24h and 2h before",
    impact: "Reduce no-shows by 60%"
  },
  "Deal Tracking & Pipeline": {
    focus: "visibility",
    icon: "BarChart",
    primarySolutions: [
      "Visual deal pipeline",
      "Stage-based task automation",
      "Revenue forecasting"
    ],
    quickWin: "Visual pipeline with deal stage tracking",
    impact: "Know exactly where each deal stands at a glance"
  },
  "Documentation & Compliance": {
    focus: "compliance",
    icon: "FileCheck",
    primarySolutions: [
      "Document checklist automation",
      "RERA compliance tracking",
      "e-Signature integration"
    ],
    quickWin: "Automated document collection checklist",
    impact: "Close deals faster with complete documentation"
  },
  "Brokerage & Commission Tracking": {
    focus: "finance",
    icon: "DollarSign",
    primarySolutions: [
      "Deal-wise commission calculation",
      "Partner payout tracking",
      "Commission reports and reconciliation"
    ],
    quickWin: "Automated commission calculation on deal closure",
    impact: "Accurate payouts keep partners motivated"
  }
};

// ==============================================
// VERTICAL-SPECIFIC DATA: Clinics / Healthcare
// ==============================================

const clinicsPainPoints = {
  "Appointment no-shows and last-minute cancellations": {
    key: "no-shows",
    severity: 3,
    solution: "Automated Appointment Reminders",
    hours_saved: 6,
    insight: "No-shows cost you ₹80,000+/month in lost revenue — reminders reduce them by 65%"
  },
  "Sending reminders and follow-ups to patients": {
    key: "patient-reminders",
    severity: 2,
    solution: "Automated Patient Communication",
    hours_saved: 5,
    insight: "Your receptionist shouldn't spend hours on calls that can be automated"
  },
  "Collecting patient history and documents before visits": {
    key: "patient-intake",
    severity: 2,
    solution: "Digital Patient Intake Forms",
    hours_saved: 4,
    insight: "Pre-visit forms save 10 minutes per patient — that adds up fast"
  },
  "Managing prescription refills and follow-up care": {
    key: "follow-up-care",
    severity: 2,
    solution: "Automated Follow-up & Recall System",
    hours_saved: 5,
    insight: "Timely follow-ups improve outcomes and build patient loyalty"
  },
  "Billing, insurance claims, and payment collection": {
    key: "billing",
    severity: 3,
    solution: "Automated Billing & Payment Collection",
    hours_saved: 8,
    insight: "Delayed billing means delayed payments — automation speeds up collections"
  },
  "Collecting reviews and feedback": {
    key: "reviews",
    severity: 2,
    solution: "Automated Review Collection",
    hours_saved: 3,
    insight: "Happy patients rarely leave reviews unless you ask — automation asks for you"
  }
};

const clinicsWorkStreams = {
  "Appointment Scheduling & Reminders": {
    focus: "efficiency",
    icon: "Calendar",
    primarySolutions: [
      "Online appointment booking",
      "WhatsApp/SMS reminders at 24h, 2h",
      "Waitlist management for cancellations"
    ],
    quickWin: "Automated WhatsApp appointment reminders",
    impact: "Reduce no-shows by 65% and recover ₹80,000+/month"
  },
  "Patient Follow-ups & Recalls": {
    focus: "care",
    icon: "Heart",
    primarySolutions: [
      "Post-visit follow-up sequences",
      "Vaccination and checkup recalls",
      "Chronic care management reminders"
    ],
    quickWin: "Automated follow-up message 48h after visit",
    impact: "Improve patient outcomes and retention"
  },
  "Patient Onboarding & Records": {
    focus: "experience",
    icon: "UserPlus",
    primarySolutions: [
      "Digital intake forms via WhatsApp",
      "Medical history collection before visit",
      "Consent form automation"
    ],
    quickWin: "Pre-visit digital intake forms",
    impact: "Save 10 minutes per patient and start consultations faster"
  },
  "Billing & Payment Collection": {
    focus: "revenue",
    icon: "CreditCard",
    primarySolutions: [
      "Automated invoice generation",
      "Payment reminders and collection",
      "Insurance claim tracking"
    ],
    quickWin: "Automated payment reminders via WhatsApp",
    impact: "Reduce payment delays by 50%"
  },
  "Prescription & Report Delivery": {
    focus: "convenience",
    icon: "FileText",
    primarySolutions: [
      "Digital prescription delivery via WhatsApp",
      "Lab report sharing",
      "Medication reminders"
    ],
    quickWin: "Auto-send prescriptions and reports via WhatsApp",
    impact: "Patients love the convenience — builds loyalty"
  },
  "Reviews & Reputation Management": {
    focus: "growth",
    icon: "Star",
    primarySolutions: [
      "Automated review requests post-visit",
      "Google review link delivery",
      "Feedback collection and analysis"
    ],
    quickWin: "Automated Google review request after positive visits",
    impact: "Build your online reputation on autopilot"
  }
};

// ==============================================
// VERTICAL-SPECIFIC DATA: Agencies / Consulting
// ==============================================

const agenciesPainPoints = {
  "Creating client reports and status updates": {
    key: "reporting",
    severity: 3,
    solution: "Automated Client Reporting",
    hours_saved: 10,
    insight: "You're spending 15+ hours/week on reports — automation can do it in minutes"
  },
  "Following up with leads and proposals": {
    key: "lead-followup",
    severity: 3,
    solution: "Automated Lead Nurturing & Proposal Follow-ups",
    hours_saved: 6,
    insight: "80% of deals need 5+ follow-ups — automation ensures you never drop the ball"
  },
  "Onboarding new clients and collecting briefs": {
    key: "onboarding",
    severity: 2,
    solution: "Digital Client Onboarding Workflows",
    hours_saved: 5,
    insight: "Smooth onboarding sets the tone for the entire relationship"
  },
  "Managing deliverables across multiple clients": {
    key: "deliverables",
    severity: 2,
    solution: "Project & Deliverable Tracking",
    hours_saved: 6,
    insight: "Missed deadlines damage trust — automation keeps everything on track"
  },
  "Tracking time and generating invoices": {
    key: "billing",
    severity: 2,
    solution: "Time Tracking & Automated Invoicing",
    hours_saved: 4,
    insight: "Poor time tracking means you're leaving money on the table"
  },
  "Coordinating with freelancers and vendors": {
    key: "vendor-coordination",
    severity: 2,
    solution: "Vendor & Freelancer Management",
    hours_saved: 4,
    insight: "Coordination overhead eats into your margins — streamline it"
  }
};

const agenciesWorkStreams = {
  "Client Reporting & Dashboards": {
    focus: "value",
    icon: "BarChart",
    primarySolutions: [
      "Automated report generation from data sources",
      "Client-facing dashboards",
      "Scheduled report delivery"
    ],
    quickWin: "Automated weekly/monthly client reports",
    impact: "Save 15+ hours/week and impress clients with consistency"
  },
  "Lead Follow-up & Proposals": {
    focus: "sales",
    icon: "TrendingUp",
    primarySolutions: [
      "Lead nurturing sequences",
      "Proposal follow-up automation",
      "Meeting scheduling links"
    ],
    quickWin: "Automated proposal follow-up sequence",
    impact: "Close 30% more deals with consistent follow-ups"
  },
  "Client Onboarding & Briefs": {
    focus: "experience",
    icon: "UserPlus",
    primarySolutions: [
      "Digital onboarding forms",
      "Brief collection workflows",
      "Welcome sequences"
    ],
    quickWin: "Structured onboarding form with brief collection",
    impact: "Start projects faster with complete information"
  },
  "Project & Deliverable Tracking": {
    focus: "delivery",
    icon: "CheckSquare",
    primarySolutions: [
      "Task management and tracking",
      "Deadline alerts and reminders",
      "Client visibility into progress"
    ],
    quickWin: "Automated deadline reminders for deliverables",
    impact: "Never miss a deadline again"
  },
  "Billing & Invoicing": {
    focus: "revenue",
    icon: "CreditCard",
    primarySolutions: [
      "Time tracking integration",
      "Automated invoice generation",
      "Payment reminders"
    ],
    quickWin: "Automated invoicing at project milestones",
    impact: "Get paid faster with consistent billing"
  },
  "Vendor & Freelancer Management": {
    focus: "operations",
    icon: "Users",
    primarySolutions: [
      "Freelancer task assignment",
      "Work submission tracking",
      "Payment tracking and reminders"
    ],
    quickWin: "Centralized freelancer task and payment tracking",
    impact: "Reduce coordination overhead and keep vendors happy"
  }
};

// Urgency scoring for lead qualification
const urgencyScores = {
  "I needed this yesterday - it's costing me money": { score: 100, label: "High Priority", emoji: "🔥" },
  "Soon - I want to fix this in the next few weeks": { score: 75, label: "Active Interest", emoji: "⚡" },
  "Planning ahead - want to sort this in 2-3 months": { score: 50, label: "Planning Phase", emoji: "📅" },
  "Just curious - exploring options for now": { score: 25, label: "Early Stage", emoji: "🔍" },
  // Vertical-specific urgency options (with em-dash)
  "I needed this yesterday — it's costing me money": { score: 100, label: "High Priority", emoji: "🔥" },
  "Soon — I want to fix this in the next few weeks": { score: 75, label: "Active Interest", emoji: "⚡" },
  "Planning ahead — want to sort this in 2-3 months": { score: 50, label: "Planning Phase", emoji: "📅" },
  "Just curious — exploring options for now": { score: 25, label: "Early Stage", emoji: "🔍" }
};

// Vertical-specific pain point and work stream mappings
const verticalPainPoints = {
  'ca-firms': caFirmsPainPoints,
  'legal': legalPainPoints,
  'real-estate': realEstatePainPoints,
  'clinics': clinicsPainPoints,
  'agencies': agenciesPainPoints
};

const verticalWorkStreams = {
  'ca-firms': caFirmsWorkStreams,
  'legal': legalWorkStreams,
  'real-estate': realEstateWorkStreams,
  'clinics': clinicsWorkStreams,
  'agencies': agenciesWorkStreams
};

export const calculateAuditResults = (answers, userInfo, vertical = null) => {
  // Extract answers
  const industryInput = answers[1] || "Other";
  const teamSize = answers[2] || "2-5";
  const goalsAndPains = answers[3] || [];
  const department = answers[4] || "Operations & Delivery";
  const urgency = answers[5] || "Just curious - exploring options for now";

  // Check if this is a vertical-specific audit
  const isVerticalAudit = vertical && verticalPainPoints[vertical];
  const painPointData = isVerticalAudit ? verticalPainPoints[vertical] : painPointAnalysis;
  const workStreamData = isVerticalAudit ? verticalWorkStreams[vertical] : departmentMapping;

  // Handle custom industry name - use "Other" data but display custom name
  // Treat "Other" selection as custom industry (even without custom text)
  const isCustomIndustry = answers.isCustomIndustry || industryInput === "Other" || !industryData[industryInput];
  // If they entered custom text, use it; otherwise show generic for "Other"
  const industry = (industryInput === "Other" && !answers.isCustomIndustry) ? "Your Business" : industryInput;
  const industryDataKey = industryData[industryInput] ? industryInput : "Other";

  // Get industry data (use "Other" benchmarks for custom industries)
  const industryInfo = industryData[industryDataKey];
  const teamInfo = teamMultipliers[teamSize] || teamMultipliers["2-5"];
  const urgencyInfo = urgencyScores[urgency] || urgencyScores["Just curious - exploring options for now"];

  // For vertical audits, use vertical-specific work stream data
  // For default audits, use department mapping
  const departmentInfo = isVerticalAudit
    ? (workStreamData[department] || Object.values(workStreamData)[0])
    : (departmentMapping[department] || departmentMapping["Operations & Delivery"]);

  // Calculate automation opportunity score based on goals and pain points
  let baseScore = 30; // Everyone has some opportunity
  let totalHoursSaved = 0;
  const prioritySolutions = [];
  const industrySpecificSolutions = [];
  const keyInsights = [];

  // Separate goals and problems for messaging
  const goals = [];
  const problems = [];

  // Analyse selected goals and pain points using appropriate data
  goalsAndPains.forEach(item => {
    const analysis = painPointData[item];
    if (analysis) {
      baseScore += analysis.severity * 8;
      totalHoursSaved += analysis.hours_saved;
      prioritySolutions.push(analysis.solution);

      // For vertical audits, all selections are problems/pain points
      if (isVerticalAudit) {
        problems.push(item);
      } else {
        // Track goals vs problems for default audit
        if (analysis.isGoal) {
          goals.push(item);
        } else {
          problems.push(item);
        }
      }

      // Add insight
      if (analysis.insight) {
        keyInsights.push(analysis.insight);
      }

      // Get industry-specific advice (for default audits)
      if (!isVerticalAudit && industryInfo.painToSolution && industryInfo.painToSolution[analysis.key]) {
        industrySpecificSolutions.push(industryInfo.painToSolution[analysis.key]);
      }
    }
  });

  // Add work stream-specific solutions
  if (isVerticalAudit && departmentInfo) {
    // For vertical audits, add work stream solutions
    if (departmentInfo.primarySolutions) {
      industrySpecificSolutions.push(...departmentInfo.primarySolutions);
    }
  } else if (industryInfo.departmentSolutions && industryInfo.departmentSolutions[department]) {
    // For default audits, add department-specific solution
    industrySpecificSolutions.push(industryInfo.departmentSolutions[department]);
  }

  // Adjust for team size (more people = more potential savings)
  totalHoursSaved = Math.round(totalHoursSaved * teamInfo.multiplier);

  // Scale score to 62-95 range (everyone has meaningful opportunity)
  // Raw baseScore ranges from 30 (no selections) to ~166 (all selected)
  // Map it so minimum is 62, maximum is 95
  const rawCapped = Math.min(baseScore, 120); // soft cap the raw score
  const automationScore = Math.round(62 + ((rawCapped - 30) / (120 - 30)) * (95 - 62));

  // Calculate cost savings
  const weeklyHours = Math.max(totalHoursSaved, 8); // Minimum 8 hours
  const monthlyCostSavings = weeklyHours * 4 * teamInfo.hourlyValue;

  // Format time savings
  const formatTimeSavings = () => {
    if (weeklyHours < 10) return `${weeklyHours}-${weeklyHours + 5} hours/week`;
    if (weeklyHours < 20) return `${Math.floor(weeklyHours * 0.8)}-${weeklyHours} hours/week`;
    return `${Math.floor(weeklyHours * 0.7)}-${weeklyHours} hours/week`;
  };

  // Format cost savings
  const formatCostSavings = () => {
    const lower = Math.floor(monthlyCostSavings * 0.7 / 5000) * 5000;
    const upper = Math.ceil(monthlyCostSavings / 5000) * 5000;
    return `₹${(lower/1000).toFixed(0)}K - ₹${(upper/1000).toFixed(0)}K/month`;
  };

  // Generate goal-based message
  const generateGoalMessage = () => {
    if (goals.length > 0) {
      if (goals.includes("I want to scale operations without increasing headcount")) {
        return "You're ready to grow - let's build systems that scale with you";
      }
      if (goals.includes("I want to free my team from repetitive work")) {
        return "Your team deserves to work on meaningful tasks - let's automate the rest";
      }
      if (goals.includes("I want faster response times for customers")) {
        return "Speed wins customers - let's make your response times lightning fast";
      }
    }
    if (problems.length > 0) {
      return "We've identified clear opportunities to save you time and money";
    }
    return "Based on your responses, we've identified significant automation opportunities";
  };

  // Generate personalised recommendations
  const recommendations = generateRecommendations(
    industry,
    goalsAndPains,
    department,
    departmentInfo,
    industryInfo
  );

  return {
    // Core results
    automationScore,
    timeSavingsPotential: formatTimeSavings(),
    costReductionPotential: formatCostSavings(),

    // Personalised content
    industry,
    isCustomIndustry,
    industryBenchmark: industryInfo,
    prioritySolutions: prioritySolutions.slice(0, 3),
    industrySpecificSolutions: industrySpecificSolutions.slice(0, 4),
    recommendations,
    keyInsights: keyInsights.slice(0, 3),

    // Department focus
    department,
    departmentInfo,
    departmentQuickWin: departmentInfo.quickWin,
    departmentImpact: departmentInfo.impact,

    // Goal-based messaging
    goalMessage: generateGoalMessage(),
    goals,
    problems,

    // Lead qualification
    urgency: urgencyInfo,
    teamSize,
    teamContext: teamInfo.context,
    goalsAndPainsCount: goalsAndPains.length,

    // User info
    userInfo
  };
};

const generateRecommendations = (industry, goalsAndPains, department, departmentInfo, industryInfo) => {
  const recommendations = [];

  // Start with department-specific quick win
  if (departmentInfo.quickWin) {
    recommendations.push(`Start with ${departmentInfo.quickWin} - ${departmentInfo.impact}`);
  }

  // Add top industry opportunity
  if (industryInfo.topOpportunities.length > 0) {
    const industryText = industry === "Your Business" ? "businesses like yours" : `${industry} businesses`;
    recommendations.push(`${industryInfo.topOpportunities[0]} - this is a top win for ${industryText}`);
  }

  // Add based on selected goals/pains
  if (goalsAndPains.includes("I want to scale operations without increasing headcount")) {
    recommendations.push("Build automation foundations now so you're ready to handle 3x volume");
  } else if (goalsAndPains.includes("I forget to follow up with leads and lose business")) {
    recommendations.push("Implement a lead follow-up sequence that runs automatically for 90 days");
  } else if (goalsAndPains.includes("I can't take a day off without things falling apart")) {
    recommendations.push("Create autonomous workflows that run reliably without your daily involvement");
  }

  // Always end with CTA
  recommendations.push("Book a free strategy call to get your personalised automation roadmap");

  return recommendations.slice(0, 4);
};

// PDF Generation Data
export const generatePDFData = (results) => {
  return {
    title: "AutomationFirst - Your Personalised Automation Report",
    company: results.userInfo.company,
    contactName: results.userInfo.name,
    email: results.userInfo.email,
    phone: results.userInfo.phone || "Not provided",
    date: new Date().toLocaleDateString('en-IN'),

    // Results
    automationScore: results.automationScore,
    timeSavingsPotential: results.timeSavingsPotential,
    costReductionPotential: results.costReductionPotential,

    // Personalised content
    priorityAreas: results.prioritySolutions,
    industrySpecificSolutions: results.industrySpecificSolutions,
    recommendedNextSteps: results.recommendations,
    keyInsights: results.keyInsights,

    // Department focus
    department: results.department,
    departmentQuickWin: results.departmentQuickWin,
    departmentImpact: results.departmentImpact,

    // Industry context
    industry: results.industry,
    isCustomIndustry: results.isCustomIndustry,
    industryBenchmark: results.industryBenchmark,
    teamSize: results.teamSize,

    // Goal-based messaging
    goalMessage: results.goalMessage,

    // CTA
    consultationEmail: "hello@automation1st.com",
    consultationPhone: "9324280636",
    consultationSubject: `Automation Strategy Call - ${results.userInfo.company}`
  };
};
